import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@chakra-ui/react";

import { getFaultyMotors, getWidgetData, getMotorFaultTypes, getStatsData, getVehicleStats } from "../../../APIs";

import { ChartLegend, SelectGroup, Switcher } from "../../../Components/Atoms";
import PopoverCard from "../../../Components/Atoms/PopoverCard";
import ReactTable from "../../../Components/Molecules/ReactTable";
import { Donut, Stack } from "../../../Components/Organisms/Graphs";
import { StoreContext } from "../../../Library/contexts";
import { getUserAndVehicle } from "../../../Library/helpers";
import { MOTOR_STATUS_HEADERS } from "../../../Library/tableHeaders";

import { STATS_ENUMS, MODULE_TYPES, processReportData, MOTOR_FAULTS } from "../../../Library/widgets";
import { Report } from "../../../Templates";

import PATHS from "../../../Routes/paths";
import { CAN_FAULT_FEEDBACK_MAP, DATE_RANGES } from "../../../Library/constants";
import { ModelPicker } from "../../../Components/Molecules";
import { DownloadIcon } from "../../../Assets/images/svgs";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";

const paths = [
  { idx: 0, name: "Analytics", link: PATHS.ANALYTICS },
  { idx: 1, name: "Motor Status", isCurrentPage: true },
];

const parser = { x: "label", y: "count", xLabel: "Types of Faults", yLabel: "Faulty Motors" };

const processData = (data, constants) => {
  // For Grouping Counts >= 5
  const processed = [];
  const legends = [];

  data.forEach((d) => {
    const faults = Array(MOTOR_FAULTS.length).fill(0);
    d.count.forEach(({ idx, count }) => {
      var faultIndex = parseInt(idx) - 1;
      faults[faultIndex] = count;
    });

    processed.push({
      color: constants.modelMap[d.model].color,
      model: constants.modelMap[d.model].nm,
      count: faults,
      modelId: d.model,
    });

    legends.push({ label: constants.modelMap[d.model].nm, color: constants.modelMap[d.model].color });
  });

  return { graphs: processed, legends };
};

const ViewReports = () => {
  const [faultyMotors, setFaultyMotors] = useState([]);
  const [totalFaultyMotors, setTotalFaultyMotors] = useState(0);
  const [motorFaultTypes, setMotorFaultTypes] = useState([]);

  const [showOverlay, setShowOverlay] = useState(false);

  const [ selected, setSelected ] = useState();
  const [ categories, setCategories ] = useState();
  const [ isLoading, setIsLoading ] = useState(true);

  const { filters, constants } = useContext(StoreContext);

  const reportRef = useRef(null);
  const widgetData = useRef(null);
  const reportData = useRef({});
  const tableData = useRef({});
  const labelData = useRef({});

  const navigate = useNavigate();
  let debounceTimer;

  const addLabelsAndColors = (data) => {
    let count = 0;

    data.forEach((motor) => {
      motor["label"] = constants.modelMap[motor._id.md].nm;
      count += motor.vehCnt;
      motor["color"] = constants.modelMap[motor._id.md].color;
      motor["modelId"] = motor._id.md;
      motor["count"] = motor.vehCnt;
    });

    setTotalFaultyMotors(count);
    setFaultyMotors(data);
  }

  useEffect(() => {
    if (reportRef.current) reportRef.current.apply();
  }, [])

  const getCardRows = (faults) => {
    return (
      faults.map(fault => {
        if(CAN_FAULT_FEEDBACK_MAP[fault]) return CAN_FAULT_FEEDBACK_MAP[fault];
        else return "";
      })
    );
  };

  const removePopover = (toggle) => {
    if (toggle) setShowOverlay((old) => !old);
    else setShowOverlay(false);
  };

  const columns = React.useMemo(() => Object.values(MOTOR_STATUS_HEADERS).map(header => {
    if (header.nm === "Faults" && selected && selected.idx === 1) return {};

    return {
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: (row) => (header.popoverCard && row.value && row.value.length) ? (
        <PopoverCard
          data={row.value.length}
          cardTitle="Faults"
          array={getCardRows(row.value)}
          removePopover={removePopover}
        />
      ) : row.value || "-",
    };
  }).filter(obj => obj.accessor), [ selected ]);

  const getSelectedRows = (selectAll, selected) => {
    if (selectAll) console.log("all selected");
    else console.log("few selected", selected);
  };

  const DonutLabel = () => {
    return (
      <span className="widget-chart-icon">
        <p className="total-faulty-motors">{ totalFaultyMotors }</p>
        <p className="total-faulty-motors-label">Faulty Motors</p>
      </span>
    )
  }

  const getSelectedFaults = (selectedFaults) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
      if (!selectedFaults.size) {
        addLabelsAndColors([]);
      } else {
        getFaultyMotors(DATE_RANGES[filters.range], [...selectedFaults].map(faultType => faultType), (failure, success) => {
          if(failure) console.error(failure)
          else addLabelsAndColors(success);
        })
      }
    }, 1000);
  }

  const getFilteredData = (data) => {
    const modelSet = new Set(filters.models);
    return data.filter((d) => modelSet.has(d.modelId));
  };

  const handleSelect = useCallback(async (option, filters, constants) => {
    if (!reportData.current[option.idx]) {
      const enums = STATS_ENUMS[MODULE_TYPES.MOTOR_STATUS];
      if (enums[option.idx].graph) {
        const query = { typ: option.idx };
        const response = await getStatsData(DATE_RANGES[filters.range], MODULE_TYPES.MOTOR_STATUS, query);
        const grouped = processReportData(response, "vehCnt");   // Grouped Model-wise
        reportData.current[option.idx] = processData(grouped, constants);
      }
    }

    if (!tableData.current[option.idx]) {
      const query = { typ: option.idx };
      const response = await getVehicleStats(DATE_RANGES[filters.range], MODULE_TYPES.MOTOR_STATUS, query);
      tableData.current[option.idx] = getUserAndVehicle(response);
    }

    setSelected(option);
  }, []);

  const handleFetchData = useCallback(async (filters, constants) => {
    setIsLoading(true);
    let switcherOptions = [];
    let categories = [];

    if (!widgetData.current) {
      // Used to Fetch Switcher Options
      const enums = STATS_ENUMS[MODULE_TYPES.MOTOR_STATUS];
      const widgetData = await getWidgetData(DATE_RANGES[filters.range], [MODULE_TYPES.MOTOR_STATUS]);
      if (!widgetData) return;

      categories = widgetData[0].data[0] || [];
      widgetData.current = categories;

      switcherOptions = [];
      Object.values(enums).forEach(async (option) => {
        let count = categories[option.idx] ? `(${categories[option.idx]})` : "";
        switcherOptions.push({
          idx: option.idx, label: `${enums[option.idx].label} ${count}`,
        });
      });
      switcherOptions.sort((a, b) => b.idx - a.idx);

      let defaultSelected = switcherOptions[0];
      reportData.current = {};
      tableData.current = {};

      await handleSelect(defaultSelected, filters, constants);
      setCategories(switcherOptions);
    }

    const labels = await getMotorFaultTypes();
    labelData.current = (labels.map(label => label.nm));
    getFaultyMotors(DATE_RANGES[filters.range], labels.map(label => label.idx), (failure, success) => {
      if (failure) console.error(failure)
      else {
        addLabelsAndColors(success);
        setMotorFaultTypes(labels);
        setIsLoading(false);
      }
    });

  }, [ handleSelect ]);

  const handleClick = (row, col) => {
    if (col == undefined) return;  // To prevent surrounding clicks error

    const newPaths = [ ...paths ];
    newPaths[paths.length - 1] = {
      ...newPaths[paths.length - 1],
      link: PATHS.ANALYTICS + "/" + PATHS.MOTOR_STATUS,
      isCurrentPage: false,
    };
    newPaths.push({ idx: 2, name: selected.label, isCurrentPage: true });

    // const graphData = getFilteredData(reportData.current[selected.idx].graphs);

    const state = {
      // model: graphData[col].modelId,
      fault: MODULE_TYPES.MOTOR_STATUS,
      title: `Motor Status - ${selected.label}`,
      paths: newPaths,
    };

    navigate({pathname: PATHS.REPORTS, search: `?faultId=${row + 1}`}, { state });
  };

  const getFaultTypes = (faults) => {
    return (faults.map(fault => {
      return CAN_FAULT_FEEDBACK_MAP[fault];
    })).join(", ");
  }

  const downloadData = (data) => {
    var sendToExcel = data.map(a => Object.assign({}, a));
    sendToExcel.forEach((vehicle, idx) => {
      vehicle["Sr No"] = idx + 1;
      vehicle["Vehicle ID"] = vehicle.idx;
      vehicle["Device ID"] = vehicle.did;
      vehicle["Vehicle Make"] = vehicle.vmk;
      vehicle["Vehicle Model"] = vehicle.mo;
      vehicle["City"] = vehicle.user.cName;
      vehicle["State"] = vehicle.user.sName;
      vehicle["User ID"] = vehicle.uid;
      vehicle["User Name"] = `${vehicle.user.fn} ${vehicle.user.ln}`;
      vehicle["User Mob"] = vehicle.user.mob;
      vehicle["User Email"] = vehicle.user.eml;
      vehicle["Faults"] = getFaultTypes(vehicle.stat.data);
    });

    var sendData = sendToExcel.map(function (exceldata) {
      var args = "Sr No, Vehicle ID, Device ID, Vehicle Make, Vehicle Model, City, State, Faults, User ID, User Name, User Mob, User Email";
      var filteredData = args.split(",").reduce((mem, arg) => {
        arg = arg.trim();
        mem[arg] = exceldata[arg];
        return mem;
      }, {});
      return filteredData;
    });
    if (sendData.length) jsonToCSVConvertor(sendData, "", true, "Motor_Status_" + Date.now(), false);
  }

  return (
    <Report breadcrumb={paths} title={"Motor Status"} isLoading={isLoading} onApply={handleFetchData} ref={reportRef}>
      {(!!motorFaultTypes.length && !isLoading) && (
        <>
          <div className="panel-chart-switch">
            <Switcher
              options={categories}
              selectedOption={selected.idx}
              handleChange={(option) => handleSelect(option, filters, constants)}
              className="panel-graph-switcher"
            />
          </div>

          {reportData.current[selected.idx] && (
            <>
              <div className="panel-chart-header">
                <ChartLegend data={reportData.current[selected.idx].legends} />
                <ModelPicker />
              </div>

              <div className="panel-chart-main motor-status-chart">
                <label className="panel-chart-label">
                  Times of Threshold Crossed
                </label>

                <Stack axis="x" data={getFilteredData(reportData.current[selected.idx].graphs)} labels={labelData.current} parser={parser} title="Fault" onClick={handleClick} />
              </div>
              <div className="seperator"></div>
              <p className="donut-title">Faulty Motors with fault combinations</p>
              <div className="faulty-motors">
                <article>
                  <div className="widget-chart">
                    <Donut title="Motor Temperature" data={getFilteredData(faultyMotors)} parser={parser} />
                    <DonutLabel />
                  </div>
                </article>
                <SelectGroup
                  label="Select Faults"
                  buttons={motorFaultTypes}
                  onChange={getSelectedFaults}
                />
              </div>
            </>
          )}

          <div className="panel-chart-sheet-header">
            <IconButton
              className="panel-button"
              aria-label='Download Report'
              icon={<DownloadIcon />}
              onClick={() => downloadData(tableData.current[selected.idx])}
            />
          </div>
          <div className="panel-chart-table">
            <ReactTable
              columns={columns}
              data={tableData.current[selected.idx]}
              showOverlay={showOverlay}
              showSearch={true}
              onRowSelect={getSelectedRows}
            />
          </div>
        </>
      )}
    </Report>
  );
};

export default ViewReports;
